import { useMemo } from 'react';
import type { ParentFieldOption } from '@atlassian/jira-issue-field-parent/src/common/types.tsx';
import { useParentField } from '@atlassian/jira-issue-field-parent/src/services/parent-field/index.tsx';
import { PARENT_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
	ListIssueForCopyPaste,
} from '../../../types.tsx';
import type { TransformedParent } from '../types.tsx';

const isTransformedParentOption = (fieldValue: unknown): fieldValue is TransformedParent =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'issueKey' in fieldValue &&
	'summary' in fieldValue &&
	'issueType' in fieldValue &&
	'issueId' in fieldValue;

const isParentOption = (fieldValue: unknown): fieldValue is ParentFieldOption =>
	fieldValue != null &&
	typeof fieldValue === 'object' &&
	'value' in fieldValue &&
	'label' in fieldValue &&
	'key' in fieldValue &&
	'fields' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
	issue: ListIssueForCopyPaste,
): ParentFieldOption | null => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, [PARENT_TYPE])
	) {
		return null;
	}

	// only paste when level is the same for source and destination issues
	if (String(issue?.issueType?.level) !== metadata.sourceIssueTypeLevel) {
		return null;
	}

	if (!isParentOption(metadata.fieldValue)) {
		return null;
	}

	return metadata.fieldValue;
};

const toParentFieldOption = (value: TransformedParent): ParentFieldOption | null => {
	const { issueKey, issueId, summary, issueType } = value;

	if (summary == null || issueType == null) {
		return null;
	}

	return {
		value: issueId,
		label: summary,
		key: issueKey,
		fields: {
			summary,
			issuetype: issueType,
		},
	};
};

const useParentClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useParentField({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		issueId: issue.issueId,
		initialValue: isTransformedParentOption(field.value) ? toParentFieldOption(field.value) : null,
	});

	const createPastedValueWithIssue = useMemo(
		() => (clipboardItem: ClipboardItem, metadata: PasteMetadata | null) =>
			getPastedValue(clipboardItem, metadata, issue),
		[issue],
	);

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.key ?? '',
		currentValue: value,
		getPastedValue: createPastedValueWithIssue,
		saveValue,
	});
};

const ParentClipboardHelper = createClipboardHelperComponent(useParentClipboardHandler);

export const parentClipboardHandler = {
	[PARENT_TYPE]: ParentClipboardHelper,
};
